import { api } from "@/lib/api"
import type { RecursoDocumento, RecursoAmbito } from "@/types/recurso-documento"
import type { RecursoDocumentoTipo } from "@/types/recurso-documento-tipo"

/**
 * Puerto tipado para los documentos de un recurso (Elemento/EPP, Vehículo o
 * Maquinaria). Mismo patrón que `PersonaDocumentosAdapter`
 * (`lib/persona-documentos-adapter.ts`), parametrizado por `ambito` +
 * `documentableId` como primeros argumentos de cada método — el backend
 * (B1-B4, ya mergeado en esta rama) resuelve la tenencia y la coherencia
 * (ámbito, tipo) del lado del servidor.
 *
 * A diferencia de `persona-documentos-adapter.ts`, acá NO hay
 * implementación mock: el backend completo (B1→B4) se implementó ANTES que
 * este slice de frontend, así que la implementación real es la única
 * necesaria (ver design `sdd/recurso-documentos` §8).
 */
export interface RecursoDocumentosUploadOptions {
  /** Reporta el progreso de subida (0-100) cuando el navegador lo soporta. */
  onProgress?: (percent: number) => void
  /** Permite cancelar la subida en curso (botón "Cancelar" del slot). */
  signal?: AbortSignal
}

export interface RecursoDocumentosVencimientoOptions {
  /** `null` = "Sin vencimiento". Fecha ISO `YYYY-MM-DD` cuando sí vence. */
  fechaVencimiento: string | null
  /** Subconjunto de {7, 15, 30}. Solo tiene efecto si `fechaVencimiento` no es null. */
  notificarDias: number[]
}

export interface RecursoDocumentosAdapter {
  listTipos(ambito: RecursoAmbito): Promise<RecursoDocumentoTipo[]>
  listDocumentos(ambito: RecursoAmbito, documentableId: number): Promise<RecursoDocumento[]>
  /**
   * Sube el archivo SOLO — sin vencimiento (siempre queda "Sin vencimiento"
   * recién subido). El vencimiento se define después vía `updateVencimiento`
   * desde el botón "Vencimiento" del slot (mismo flujo que
   * `persona_documentos` — decisión UX #1, obs #1318).
   */
  upload(
    ambito: RecursoAmbito,
    documentableId: number,
    tipoDocumentoId: number,
    file: File,
    opts?: RecursoDocumentosUploadOptions,
  ): Promise<RecursoDocumento>
  /** Recibe el `documento` completo (no solo el id) — el llamador ya lo tiene
   * en mano (viene del listado renderizado), evitando un refetch extra solo
   * para resolver `filename_original`. */
  download(ambito: RecursoAmbito, documentableId: number, documento: RecursoDocumento): Promise<void>
  destroy(ambito: RecursoAmbito, documentableId: number, documentoId: number): Promise<void>
  /** `PATCH /recursos/{ambito}/{documentableId}/documentos/{documentoId}` —
   * fija o limpia el vencimiento de un documento ya subido, independiente
   * de la subida. */
  updateVencimiento(
    ambito: RecursoAmbito,
    documentableId: number,
    documentoId: number,
    opts: RecursoDocumentosVencimientoOptions,
  ): Promise<RecursoDocumento>
}

function documentosBase(ambito: RecursoAmbito, documentableId: number): string {
  return `/recursos/${ambito}/${documentableId}/documentos`
}

export const apiRecursoDocumentosAdapter: RecursoDocumentosAdapter = {
  async listTipos(ambito) {
    const res = await api.get<{ data: RecursoDocumentoTipo[] }>(
      `/recursos/${ambito}/documento-tipos`,
    )
    return res.data
  },

  async listDocumentos(ambito, documentableId) {
    const res = await api.get<{ data: RecursoDocumento[] }>(documentosBase(ambito, documentableId))
    return res.data
  },

  async upload(ambito, documentableId, tipoDocumentoId, file, opts) {
    const fd = new FormData()
    fd.append("archivo", file)
    fd.append("tipo_documento_id", String(tipoDocumentoId))
    // Sin `fecha_vencimiento`/`notificar_dias` — el backend defaultea a
    // "Sin vencimiento" cuando no vienen. El vencimiento se fija después vía
    // `updateVencimiento` (botón "Vencimiento" del slot).
    const res = await api.uploadForm<{ data: RecursoDocumento }>(
      documentosBase(ambito, documentableId),
      fd,
      { onProgress: opts?.onProgress, signal: opts?.signal },
    )
    return res.data
  },

  async download(ambito, documentableId, documento) {
    // El nombre de descarga lo define el SERVER (Content-Disposition). En
    // una descarga por blob el atributo `a.download` gana sobre el header,
    // así que leemos el nombre del server con `blobWithFilename` y sólo
    // caemos a `filename_original` si no llegara (mismo patrón que
    // `persona-documentos-adapter.ts`).
    const { blob, filename } = await api.blobWithFilename(
      `${documentosBase(ambito, documentableId)}/${documento.id}/download`,
    )
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename ?? documento.filename_original
    document.body.appendChild(a)
    a.click()
    a.remove()
    // Revoke diferido: algunos navegadores tratan la descarga disparada por
    // `a.click()` como asíncrona y revocar el object URL de inmediato puede
    // cancelarla a mitad de camino.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  },

  async destroy(ambito, documentableId, documentoId) {
    await api.deleteJson(`${documentosBase(ambito, documentableId)}/${documentoId}`)
  },

  async updateVencimiento(ambito, documentableId, documentoId, opts) {
    const res = await api.patch<{ data: RecursoDocumento }>(
      `${documentosBase(ambito, documentableId)}/${documentoId}`,
      {
        fecha_vencimiento: opts.fechaVencimiento,
        notificar_dias: opts.fechaVencimiento ? opts.notificarDias : [],
      },
    )
    return res.data
  },
}
